<template>
  <teleport to="body">
    <div class="custom-chat-ui">
      <!-- Floating Chat Button -->
      <template v-if="!isOpen && !hideLauncher">
        <!-- Mobile text CTA + scroll-driven show/hide — commented out. We now use
             the always-visible icon FAB (below) on every breakpoint, matching the
             desktop treatment.
        <MyButton
          v-if="isMobile"
          class="chat-button chat-button--mobile"
          :class="{ 'chat-button--hidden': !mobileButtonVisible }"
          label="Ask about Jacques’ work"
          type="solid"
          size="large"
          :aria-label="buttonLabel"
          @click="openChat"
        />
        -->
        <!-- Chat FAB: icon-only floating button (chat bubble), always visible on all breakpoints -->
        <MyButton
          class="chat-button"
          type="ghost"
          size="small"
          hideLabel
          :style="buttonStyle"
          :aria-label="buttonLabel"
          @click="openChat"
        >
          <img
            v-if="buttonIconSrc"
            :src="buttonIconSrc"
            :alt="buttonLabel"
            class="chat-button-icon"
          />
          <svg
            v-else
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5"
            class="chat-button-icon"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        </MyButton>
        <!-- On a page with its own questions, the first one shows beside the
             button for a few seconds after you arrive, once a visit. Tapping
             it opens the chat and asks it. -->
        <Transition name="chat-teaser">
          <button
            v-if="teaser"
            type="button"
            class="chat-teaser"
            :style="teaserStyle"
            @click="askTeaser"
          >
            {{ teaser }}
          </button>
        </Transition>
        <!-- Desktop text button (rotated) — saved for reference:
        <div class="chat-button-desktop-wrapper">
          <MyButton
            class="chat-button--desktop"
            label="Ask about Jacques’ work"
            type="solid"
            size="large"
            :aria-label="buttonLabel"
            @click="openChat"
          />
        </div>
        -->
      </template>

      <!-- Chat Window -->
      <!-- Mobile opens as a sheet sliding up from the bottom; desktop keeps the
           scale-from-corner pop. Fullscreen state is reset in onChatAfterLeave,
           not in closeChat, so the window keeps its layout while it animates out. -->
      <transition name="chat-backdrop">
        <div v-if="isOpen && isFullscreen" class="chat-backdrop" aria-hidden="true" />
      </transition>
      <transition :name="isMobile ? 'chat-sheet' : 'chat-window'" @after-leave="onChatAfterLeave">
        <div
          v-if="isOpen"
          class="chat-window"
          :class="[
            `chat-window--pos-${buttonPosition}`,
            { 'chat-window--fullscreen': isFullscreen },
          ]"
          :style="windowStyle"
        >
          <!-- Chat header — mirrors HeaderNav: .bg (padding) > nav (grid) > .nav-left + ul -->
          <div class="chat-floating-actions">
            <!-- Mobile: laid out like a sheet (✕ · title · Clear) so it doesn't read
                 as a second copy of the home header. -->
            <nav v-if="isMobile" class="chat-nav chat-nav--sheet">
              <MyButton
                class="chat-nav-close"
                type="ghost"
                size="small"
                hideLabel
                aria-label="Close chat"
                @click="closeChat"
              >
                <!-- The menu icon's two bars, crossed. They arrive as the
                     menu's = and cross as the sheet lands. -->
                <span class="chat-nav-close-icon" aria-hidden="true"><span></span><span></span></span>
              </MyButton>
              <p class="chat-header-title">Rambot</p>
              <TextLink
                class="chat-nav-clear"
                label="Clear"
                aria-label="Clear conversation"
                @click="clearChat"
              />
            </nav>
            <nav v-else class="chat-nav">
              <div class="chat-nav-left">
                <!-- <router-link to="/" class="chat-nav-avatar-link" aria-label="View resume">
                  <img
                    class="chat-nav-avatar"
                    src="@/assets/images/portrait.jpg"
                    draggable="false"
                    alt="Jacques Ramphal"
                  />
                </router-link> -->
                <p class="chat-header-title">Rambot</p>
              </div>
              <ul class="chat-nav-links">
                <li v-if="allowFullscreen && !isMobile">
                  <TextLink
                    :label="isFullscreen ? 'Minimize' : 'Expand'"
                    :aria-label="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
                    @click="toggleFullscreen"
                  />
                </li>
                <li>
                  <TextLink label="Clear" aria-label="Clear conversation" @click="clearChat" />
                </li>
                <li>
                  <TextLink label="Close" aria-label="Close chat" @click="closeChat" />
                </li>
              </ul>
            </nav>
          </div>

          <!-- Chat Messages Container -->
          <div class="chat-messages" ref="messagesContainer">
            <!-- Welcome Message -->
            <div
              v-if="messages.length === 0 && welcomeMessage"
              class="chat-message chat-message--welcome"
            >
              <p class="chat-welcome-text">{{ welcomeMessage }}</p>
            </div>

            <!-- Starter Prompts -->
            <div
              v-if="messages.length === 0 && quickPrompts.length > 0"
              class="chat-starter-prompts"
            >
              <MyButton
                v-for="(prompt, index) in quickPrompts"
                :key="index"
                class="chat-starter-prompt"
                type="ghost"
                size="small"
                :label="prompt"
                @click="sendMessage(prompt)"
              >
              </MyButton>
            </div>

            <!-- Messages. The group wrapper stacks the bubble and any cards
                 that came with it; it repeats the container's own gap so the
                 spacing between messages is unchanged from before cards. -->
            <div
              v-for="(message, index) in renderedMessages"
              :key="index"
              class="chat-message-group"
            >
              <div
                class="chat-message"
                :class="{
                  'chat-message--bot': message.role === 'bot',
                  'chat-message--user': message.role === 'user',
                }"
              >
                <div
                  class="chat-message-bubble"
                  :class="{
                    'chat-message-bubble--bot': message.role === 'bot',
                    'chat-message-bubble--user': message.role === 'user',
                  }"
                  :style="message.role === 'bot' ? botMessageStyle : userMessageStyle"
                  v-html="formatMessage(message.text, message.role)"
                ></div>
              </div>

              <!-- Cards for the pieces this reply pointed at. A sibling of the
                   bubble rather than a child, so they get the full chat width
                   instead of the bubble's inset and background. -->
              <ul v-if="message.cards.length" class="chat-cards">
                <li v-for="card in message.cards" :key="card.route" class="chat-card-item">
                  <a
                    class="chat-card"
                    :class="{ 'chat-card--text-only': !card.image }"
                    :href="card.route"
                    @click="handleCardClick(card, $event)"
                  >
                    <!-- Decorative: the title beside it already names the piece,
                         so a described thumbnail would just be read twice. -->
                    <img
                      v-if="card.image"
                      class="chat-card-thumb"
                      :src="card.image"
                      alt=""
                      loading="lazy"
                    />
                    <span class="chat-card-body">
                      <span v-if="card.eyebrow" class="chat-card-eyebrow">{{ card.eyebrow }}</span>
                      <span class="chat-card-title">{{ card.title }}</span>
                      <span v-if="card.description" class="chat-card-description">{{
                        card.description
                      }}</span>
                    </span>
                  </a>
                </li>
              </ul>
            </div>

            <!-- Loading Indicator -->
            <div v-if="isLoading" class="chat-message chat-message--bot">
              <div class="chat-loading">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="chat-error">
              <p>{{ error }}</p>
            </div>
          </div>

          <!-- The page the guide can see, so "this page" has a referent. -->
          <p v-if="pageLabel" class="chat-page-indicator" aria-live="polite">
            <span class="chat-page-indicator-dot" aria-hidden="true"></span>
            <span class="chat-page-indicator-label">Viewing</span>
            <span class="chat-page-indicator-title">{{ pageLabel }}</span>
          </p>

          <!-- Chat Input -->
          <div class="chat-input-container" :style="inputContainerStyle">
            <MyInput
              class="chat-input-wrapper"
              v-model="inputMessage"
              type="text"
              name="chat"
              :placeholder="placeholderText"
              :disabled="isLoading"
              :required="false"
              :hideLabel="true"
              :maxlength="maxChars || undefined"
              autocomplete="off"
              inputClass="chat-input"
              :style="inputStyle"
              :submitButton="true"
              :submitDisabled="!canSend || isLoading"
              @keyup.enter="handleSend"
              @input="handleInput"
              @submit="handleSend"
              ref="inputRef"
            />
          </div>

          <!-- Character Limit Warning -->
          <div v-if="showCharWarning" class="chat-warning">
            <p>{{ maxCharsWarningMessage }}</p>
          </div>
        </div>
      </transition>
    </div>
  </teleport>
</template>

<script>
// `marked` is loaded lazily (dynamic import) the first time the chat opens, so
// it stays out of the initial page bundle — this widget mounts on every page.
import MyButton from './Button/Button.vue';
import MyInput from './form/MyInput.vue';
import TextLink from './text/TextLink.vue';
import chatCards from '../assets/data/chat-cards.json';

// Rich replies. The bot asks for a card by writing [[card:/doc/slug]] — a key,
// never a URL — and we resolve it here against the generated manifest
// (scripts/build-chat-context.mjs). Nothing the model writes reaches the DOM as
// markup or as an image src, so the escape-then-marked pipeline in
// formatMessage() stays intact and a hallucinated route resolves to nothing.
const CARD_MANIFEST = (chatCards && chatCards.cards) || {};
const CARD_MARKER_RE = /\[\[\s*card\s*:\s*([^\]]+?)\s*\]\]/gi;
const MAX_CARDS_PER_MESSAGE = 3;

// What the chat offers depends on the kind of page you're on. The widget
// sends the page's path and title with every message, and the API tells the
// model that "this page" means it (chat-api/api/chat.js, currentPage), so
// these can say "this" and mean the page. Types come from the library
// (chat-cards.json `type`) or, for pages with no card, from the route.
const PAGE_TYPES = {
  article: {
    placeholder: 'Ask about this article',
    prompts: [
      'Sum up this article',
      'What’s the argument here?',
      'What should I read after this?',
    ],
  },
  'case-study': {
    placeholder: 'Ask about this case study',
    prompts: [
      'What was the problem here?',
      'What did Jacques do on this project?',
      'What came out of it?',
    ],
  },
  tool: {
    placeholder: 'Ask about this tool',
    prompts: ['What does this do?', 'How would I use it?', 'Why did Jacques build it?'],
  },
  about: {
    placeholder: 'Ask about Jacques',
    prompts: [
      'How did Jacques get into design?',
      'What kind of work does he do?',
      'How does he work with a team?',
    ],
  },
  index: {
    placeholder: (label) => `Ask about the ${label}`,
    prompts: ['Where should I start?', 'Which case study should I read first?'],
  },
  page: {
    placeholder: (label) => `Ask about ${label}`,
    prompts: ['Tell me about this page', 'What should I read after this?'],
  },
};
const ROUTE_TYPES = [
  [/^\/(doc\/info|info|about|resume|hire)$/, 'about'],
  [/^\/(library|writing|work|play|explorations|sketchbook|links)$/, 'index'],
];

// Must match normalizeRoute() in scripts/build-chat-context.mjs, or the model's
// marker and the manifest key miss each other over a trailing slash or a capital.
const normalizeRoute = (route) =>
  '/' +
  String(route || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');

export default {
  name: 'CustomChatUI',
  components: { MyButton, MyInput, TextLink },
  props: {
    // Hides the launcher (on pages meant for reading) without unmounting, so
    // a chat already open stays open, with its history, as you move around.
    hideLauncher: {
      type: Boolean,
      default: false,
    },
    // Webhook Configuration
    webhookUrl: {
      type: String,
      default: 'https://jacquesramphal-github-io.vercel.app/api/chat',
    },
    metadata: {
      type: Object,
      default: () => ({}),
    },

    // Button Configuration
    buttonPosition: {
      type: String,
      default: 'bottom-right', // 'bottom-right', 'bottom-left', 'top-right', 'top-left'
    },
    buttonSize: {
      type: Number,
      default: 50,
    },
    buttonIconSrc: {
      type: String,
      // Keep existing default icon unless overridden by parent
      default: '',
    },
    buttonBackgroundColor: {
      type: String,
      default: 'var(--background)',
    },
    buttonLabel: {
      type: String,
      default: 'Open chat',
    },

    // Window Configuration
    width: {
      type: [Number, String],
      default: 400,
    },
    height: {
      type: [Number, String],
      default: 600,
    },
    backgroundColor: {
      type: String,
      default: 'var(--background)',
    },
    borderRadius: {
      type: String,
      default: 'var(--size-3)', // design-guard:ignore
    },
    allowFullscreen: {
      type: Boolean,
      default: true,
    },
    autoFullscreenOnDesktop: {
      type: Boolean,
      default: false,
    },
    desktopBreakpoint: {
      type: Number,
      default: 1024,
    },
    mobileFullscreenBreakpoint: {
      type: Number,
      default: 768,
    },
    fullscreenOnMobile: {
      type: Boolean,
      default: true,
    },

    // Messages Configuration
    welcomeMessage: {
      type: String,
      default:
        'Hi, I’m the guide to Jacques’ work. Ask about a project, something he’s written, or how he works, and I’ll point you to it.',
    },
    errorMessage: {
      type: String,
      default: 'Sorry, I’m having trouble responding right now. Please try again in a moment.',
    },
    starterPrompts: {
      type: Array,
      default: () => [
        'Which case study should I read first?',
        'What’s Genie, and why did Jacques build it?',
        'Does Jacques write code?',
        'What’s “Taste is triage” about?',
      ],
    },
    starterPromptFontSize: {
      type: Number,
      default: 15,
    },
    fontSize: {
      type: Number,
      default: 16,
    },
    renderHTML: {
      type: Boolean,
      default: false,
    },
    clearChatOnReload: {
      type: Boolean,
      default: false,
    },

    // Bot Message Styling
    botMessageBackgroundColor: {
      type: String,
      default: 'var(--background-darker)',
    },
    botMessageTextColor: {
      type: String,
      default: 'var(--foreground)',
    },
    botMessageBorderRadius: {
      type: Number,
      default: 6,
    },

    // User Message Styling
    userMessageBackgroundColor: {
      type: String,
      default: 'var(--background)',
    },
    userMessageTextColor: {
      type: String,
      default: 'var(--foreground)',
    },
    userMessageBorderRadius: {
      type: Number,
      default: 6,
    },

    // Input Configuration
    inputPlaceholder: {
      type: String,
      default: 'Ask about his work or writing',
    },
    inputBackgroundColor: {
      type: String,
      default: 'var(--background)',
    },
    inputTextColor: {
      type: String,
      default: 'var(--foreground)',
    },
    inputBorderRadius: {
      type: Number,
      default: 6,
    },
    sendButtonColor: {
      type: String,
      default: 'var(--link)',
    },
    sendButtonBorderRadius: {
      type: Number,
      default: 50,
    },
    maxChars: {
      type: Number,
      default: null,
    },
    maxCharsWarningMessage: {
      type: String,
      default: 'You exceeded the character limit.',
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      isFullscreen: false,
      fullscreenForcedByMobile: false,
      viewportWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      messages: [],
      inputMessage: '',
      isLoading: false,
      error: null,
      showCharWarning: false,
      sessionId: null,
      bodyScrollLocked: false,
      scrollLockY: 0,
      atBottomOfPage: false,
      menuIsOpen: false,
      markedLib: null,
      pageLabel: '',
      teaser: '',
      teaserAbove: false,
      teaserMini: false,
      teasedRoutes: [],
    };
  },
  created() {
    this.sessionId = this.getOrCreateSessionId();
    try {
      this.teasedRoutes = JSON.parse(sessionStorage.getItem('chat-teased-routes') || '[]');
    } catch (e) {
      this.teasedRoutes = [];
    }
  },
  computed: {
    // Home keeps the general prompts. A page type with three of its own shows
    // just those; one with fewer is topped up with the general ones.
    pageType() {
      const path = normalizeRoute(this.$route?.path);
      if (path === '/' || !this.pageLabel) return null;
      const card = CARD_MANIFEST[path];
      if (card && card.type) return card.type === 'design-project' ? 'case-study' : card.type;
      const match = ROUTE_TYPES.find(([re]) => re.test(path));
      return match ? match[1] : 'page';
    },
    pageConfig() {
      return this.pageType ? PAGE_TYPES[this.pageType] || PAGE_TYPES.page : null;
    },
    // The page's own prompts (library.json `chatPrompts`) win; otherwise its
    // type's.
    pagePrompts() {
      const card = CARD_MANIFEST[normalizeRoute(this.$route?.path)];
      return this.pageType && card && card.prompts ? card.prompts : null;
    },
    // Beside the button, at its height, so the two read as one thing.
    teaserStyle() {
      // On phones the beat player can hold the rest of the bottom row, so
      // when it's there the label sits above the button instead, on its
      // right edge. The button's outer box is its size plus a 2px border.
      const place = this.teaserAbove
        ? {
            bottom: `calc(var(--size-5) + ${this.buttonSize + 4}px + var(--spacing-xxs))`,
            right: 'var(--size-5)',
          }
        : {
            bottom: 'var(--size-5)',
            right: `calc(var(--size-5) + ${this.buttonSize}px + var(--spacing-xxs))`,
          };
      return {
        ...place,
        // Content height is the button's, and the border is the button's,
        // so the two outer boxes come out the same height.
        height: `${this.buttonSize}px`,
        lineHeight: `${this.buttonSize}px`,
        // Beside the button it stops short of the beat player's square, if
        // that's in the other corner.
        maxWidth: this.teaserMini
          ? // Content width (it's content-box): the screen less both margins,
            // both squares with their borders, a gap either side, and the
            // label's own padding and border.
            `calc(100vw - var(--size-5) * 2 - ${(this.buttonSize + 4) * 2}px - var(--spacing-xxs) * 2 - var(--spacing-xs) * 2 - 4px)`
          : `calc(100vw - ${this.buttonSize}px - var(--size-5) * 2 - var(--spacing-xxs) - var(--spacing-xs))`,
      };
    },
    quickPrompts() {
      if (this.pagePrompts) return this.pagePrompts;
      if (!this.pageConfig) return this.starterPrompts;
      const own = this.pageConfig.prompts;
      if (own.length >= 3) return own;
      const rest = this.starterPrompts.filter((p) => !own.includes(p));
      return [...own, ...rest].slice(0, 4);
    },
    placeholderText() {
      const p = this.pageConfig && this.pageConfig.placeholder;
      if (!p) return this.inputPlaceholder;
      return typeof p === 'function' ? p(this.pageLabel) : p;
    },
    isDesktop() {
      return this.viewportWidth >= this.desktopBreakpoint;
    },
    isMobile() {
      return this.viewportWidth <= this.mobileFullscreenBreakpoint;
    },
    // mobileButtonVisible() — drove the show/hide of the old mobile text CTA.
    // Commented out along with that button; the icon FAB is always visible now.
    // mobileButtonVisible() {
    //   const isHome = this.$route && this.$route.path === '/';
    //   return isHome || this.atBottomOfPage || this.menuIsOpen;
    // },
    // Display form of the conversation: card markers pulled out of the text and
    // resolved to card data. Derived rather than stored, so what persists to
    // localStorage stays the model's original reply — a card whose entry was
    // unpublished since then just stops rendering, and no stale thumbnail path
    // outlives a rebuild.
    renderedMessages() {
      return this.messages.map((message) => {
        const { text, routes } = this.splitCardMarkers(message.text, message.role);
        return {
          ...message,
          text,
          // Cap after resolving, not before: an unknown route should cost
          // nothing, rather than spending one of the three slots on nothing.
          cards: routes
            .map((route) => this.resolveCard(route))
            .filter(Boolean)
            .slice(0, MAX_CARDS_PER_MESSAGE),
        };
      });
    },
    buttonStyle() {
      const positions = {
        // Match the old inline widget spacing (20px edge) // design-guard:ignore
        // Uses your numeric scale: --size-5 == 2rem (20px since html is 10px) // design-guard:ignore
        'bottom-right': { bottom: 'var(--size-5)', right: 'var(--size-5)' },
        'bottom-left': { bottom: 'var(--size-5)', left: 'var(--size-5)' },
        'top-right': { top: 'var(--size-5)', right: 'var(--size-5)' },
        'top-left': { top: 'var(--size-5)', left: 'var(--size-5)' },
      };

      return {
        width: `${this.buttonSize}px`,
        height: `${this.buttonSize}px`,
        ...positions[this.buttonPosition],
      };
    },
    buttonPositionStyle() {
      const positions = {
        'bottom-right': { bottom: 'var(--size-5)', right: 'var(--size-5)' },
        'bottom-left': { bottom: 'var(--size-5)', left: 'var(--size-5)' },
        'top-right': { top: 'var(--size-5)', right: 'var(--size-5)' },
        'top-left': { top: 'var(--size-5)', left: 'var(--size-5)' },
      };
      return positions[this.buttonPosition] || {};
    },
    windowStyle() {
      if (this.isFullscreen) {
        return {
          width: '100vw', // design-guard:ignore
          height: '100vh', // design-guard:ignore
          maxWidth: '100vw', // design-guard:ignore
          maxHeight: '100vh', // design-guard:ignore
          borderRadius: '0',
          bottom: '0',
          right: '0',
          top: '0',
          left: '0',
        };
      }

      const edge = 'var(--size-5)'; // 20px // design-guard:ignore
      const positions = {
        'bottom-right': {
          right: edge,
          bottom: edge,
        },
        'bottom-left': {
          left: edge,
          bottom: edge,
        },
        'top-right': {
          right: edge,
          top: edge,
        },
        'top-left': {
          left: edge,
          top: edge,
        },
      };

      return {
        width: typeof this.width === 'number' ? `${this.width}px` : this.width,
        height: typeof this.height === 'number' ? `${this.height}px` : this.height,
        backgroundColor: this.backgroundColor,
        borderRadius: this.borderRadius,
        ...positions[this.buttonPosition],
      };
    },
    botMessageStyle() {
      return {
        // Bot: no background/border (styling is handled by CSS class)
        backgroundColor: 'transparent',
        color: this.botMessageTextColor,
        borderRadius: `${this.botMessageBorderRadius}px`,
      };
    },
    userMessageStyle() {
      return {
        // User: light grey background, no border (styling is handled by CSS class)
        backgroundColor: 'transparent',
        color: this.userMessageTextColor,
        borderRadius: `${this.userMessageBorderRadius}px`,
      };
    },
    inputContainerStyle() {
      return {
        backgroundColor: this.inputBackgroundColor,
      };
    },
    inputStyle() {
      return {
        backgroundColor: this.inputBackgroundColor,
        color: this.inputTextColor,
        borderRadius: `${this.inputBorderRadius}px`,
      };
    },
    sendButtonStyle() {
      return {
        color: this.sendButtonColor,
        borderRadius: `${this.sendButtonBorderRadius}px`,
      };
    },
    canSend() {
      if (!this.inputMessage.trim()) return false;
      if (this.maxChars && this.inputMessage.length > this.maxChars) return false;
      return true;
    },
  },
  methods: {
    generateSessionId() {
      return Date.now().toString(36) + Math.random().toString(36).slice(2, 11);
    },
    lockBodyScroll() {
      if (this.bodyScrollLocked) return;
      if (typeof window === 'undefined' || typeof document === 'undefined') return;

      this.scrollLockY = window.scrollY || window.pageYOffset || 0;

      // More reliable than just `overflow: hidden` (esp. iOS Safari)
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollLockY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';

      this.bodyScrollLocked = true;
    },
    unlockBodyScroll() {
      if (!this.bodyScrollLocked) return;
      if (typeof window === 'undefined' || typeof document === 'undefined') return;

      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.width = '';

      window.scrollTo(0, this.scrollLockY || 0);
      this.bodyScrollLocked = false;
    },
    syncFullscreenRootClass() {
      // HeaderNav sits at z-index: 100000; make fullscreen chat reliably overlay it.
      const cls = 'chat-fullscreen-open';
      if (typeof document === 'undefined') return;
      if (this.isFullscreen) {
        document.documentElement.classList.add(cls);
        this.lockBodyScroll();
      } else {
        document.documentElement.classList.remove(cls);
        this.unlockBodyScroll();
      }
    },
    getOrCreateSessionId() {
      const storageKey = `chat-session-${this.webhookUrl}`;
      if (this.clearChatOnReload) {
        try {
          localStorage.removeItem(storageKey);
        } catch (e) {
          // ignore
        }
        return this.generateSessionId();
      }

      try {
        const existing = localStorage.getItem(storageKey);
        if (existing) return existing;
        const created = this.generateSessionId();
        localStorage.setItem(storageKey, created);
        return created;
      } catch (e) {
        return this.generateSessionId();
      }
    },
    ensureMarked() {
      // Lazily code-split `marked` out of the initial page bundle. Setting
      // this.markedLib (reactive) re-renders any already-shown bot messages.
      if (this.markedLib || this._markedLoading) return;
      this._markedLoading = true;
      import('marked')
        .then((m) => {
          this.markedLib = m.marked;
        })
        .catch(() => {
          this._markedLoading = false;
        });
    },
    openChat() {
      this.ensureMarked();
      this.isOpen = true;
      // On mobile, default to fullscreen (so we also lock scroll + overlay nav).
      // On desktop, always open in the windowed state (fullscreen only when user selects it).
      if (this.fullscreenOnMobile && this.isMobile) {
        this.isFullscreen = true;
        this.fullscreenForcedByMobile = true;
      } else {
        this.isFullscreen = false;
        this.fullscreenForcedByMobile = false;
      }
      this.syncFullscreenRootClass();
      if (!this.isMobile) {
        this.$nextTick(() => {
          try {
            if (this.$refs.inputRef) {
              this.$refs.inputRef.focus();
            }
          } catch (e) {
            // iOS Safari blocks programmatic focus outside a synchronous gesture
          }
        });
      }
    },
    closeChat() {
      this.isOpen = false;
    },
    onChatAfterLeave() {
      // Deferred from closeChat so the window doesn't drop out of fullscreen
      // (and the page doesn't unlock scroll) halfway through the leave animation.
      if (this.isOpen) return;
      this.isFullscreen = false;
      this.fullscreenForcedByMobile = false;
      this.syncFullscreenRootClass();
    },
    clearChat() {
      // Wipe the visible conversation and its saved copy...
      this.messages = [];
      this.error = null;
      this.inputMessage = '';
      try {
        localStorage.removeItem(`chat-messages-${this.webhookUrl}`);
      } catch (e) {
        // ignore
      }
      // ...and start a fresh session so the server-side memory (KV) also
      // resets — otherwise the bot would still "remember" the cleared turns.
      this.sessionId = this.generateSessionId();
      try {
        localStorage.setItem(`chat-session-${this.webhookUrl}`, this.sessionId);
      } catch (e) {
        // ignore
      }
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      // Any manual toggle means fullscreen state is user-driven now.
      this.fullscreenForcedByMobile = false;
      this.syncFullscreenRootClass();
      this.$nextTick(() => {
        this.scrollToBottom();
        // Focus input when entering fullscreen (desktop only — iOS blocks async focus)
        if (this.isFullscreen && !this.isMobile && this.$refs.inputRef) {
          try {
            this.$refs.inputRef.focus();
          } catch (e) {
            // ignore
          }
        }
      });
    },
    handleInput() {
      if (this.maxChars && this.inputMessage.length > this.maxChars) {
        this.showCharWarning = true;
        setTimeout(() => {
          this.showCharWarning = false;
        }, 3000);
      } else {
        this.showCharWarning = false;
      }
    },
    async handleSend() {
      if (!this.canSend || this.isLoading) return;

      const messageText = this.inputMessage.trim();
      this.inputMessage = '';
      this.showCharWarning = false;

      // Add user message
      this.messages.push({
        role: 'user',
        text: messageText,
        timestamp: new Date(),
      });

      this.$nextTick(() => {
        this.scrollToBottom();
      });

      // Send to webhook
      this.isLoading = true;
      this.error = null;

      try {
        const response = await fetch(this.webhookUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            // Match the n8nchatui widget payload shape
            action: 'sendMessage',
            chatInput: messageText,
            sessionId: this.sessionId,
            metadata: this.metadataNow(),
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Handle various n8n response formats
        let botResponse = this.errorMessage;

        if (typeof data === 'string') {
          botResponse = data;
        } else if (data?.response) {
          botResponse = data.response;
        } else if (data?.message) {
          botResponse = data.message;
        } else if (data?.text) {
          botResponse = data.text;
        } else if (data?.output) {
          // n8n sometimes wraps responses in output
          botResponse =
            typeof data.output === 'string'
              ? data.output
              : data.output?.message || data.output?.response || this.errorMessage;
        } else if (Array.isArray(data) && data.length > 0) {
          // Handle array responses
          botResponse =
            typeof data[0] === 'string'
              ? data[0]
              : data[0]?.message || data[0]?.response || this.errorMessage;
        } else if (data?.data) {
          botResponse =
            typeof data.data === 'string'
              ? data.data
              : data.data?.message || data.data?.response || this.errorMessage;
        }

        // Add bot message
        this.messages.push({
          role: 'bot',
          text: botResponse,
          timestamp: new Date(),
        });

        this.$nextTick(() => {
          this.scrollToBottom();
        });
      } catch (error) {
        console.error('Chat error:', error);
        this.error = this.errorMessage;
        this.messages.push({
          role: 'bot',
          text: this.errorMessage,
          timestamp: new Date(),
        });
      } finally {
        this.isLoading = false;
      }
    },
    sendMessage(message) {
      this.inputMessage = message;
      this.handleSend();
    },
    // Pull [[card:…]] markers out of a bot reply. Returns the text to display
    // and the routes the markers named, deduped and normalized to the
    // manifest's spelling. User messages are left alone: a visitor typing the
    // marker syntax should see it back, not have it interpreted.
    splitCardMarkers(text, role) {
      const raw = String(text == null ? '' : text);
      if (role !== 'bot' || raw.indexOf('[[') === -1) return { text: raw, routes: [] };
      const routes = [];
      const stripped = raw.replace(CARD_MARKER_RE, (_match, route) => {
        const key = normalizeRoute(route);
        if (!routes.includes(key)) routes.push(key);
        return '';
      });
      return {
        // Markers sit on their own lines, so removing them leaves blank lines
        // that `breaks: true` would otherwise render as trailing <br>s.
        text: stripped.replace(/\n{3,}/g, '\n\n').trim(),
        routes,
      };
    },
    // Unknown route means no card. That covers a route the model invented, one
    // whose entry has since been unpublished, and one saved to localStorage
    // before the manifest changed.
    // The library title when the page has a card, otherwise the tab title
    // without the site name. Home is just "Home".
    updatePageLabel() {
      const path = normalizeRoute(this.$route?.path ?? window.location.pathname);
      if (path === '/') {
        this.pageLabel = 'Home';
        return;
      }
      const card = CARD_MANIFEST[path];
      if (card && card.title) {
        this.pageLabel = card.title;
        return;
      }
      const title = (typeof document !== 'undefined' ? document.title : '')
        .replace(/\s*[|·–—-]\s*Jacques Ramphal\s*$/i, '')
        .trim();
      this.pageLabel = title && !/^Jacques Ramphal/i.test(title) ? title : '';
    },
    // A beat after you arrive on a page with its own prompts, its first
    // question shows beside the button for six seconds. Once per page a visit,
    // never while the chat is open or the button is hidden.
    scheduleTeaser() {
      clearTimeout(this.teaserTimer);
      clearTimeout(this.teaserHideTimer);
      this.teaser = '';
      this.teaserTimer = setTimeout(() => {
        const path = normalizeRoute(this.$route?.path);
        if (this.isOpen || this.hideLauncher || !this.pagePrompts) return;
        if (this.teasedRoutes.includes(path)) return;
        this.teasedRoutes.push(path);
        try {
          sessionStorage.setItem('chat-teased-routes', JSON.stringify(this.teasedRoutes));
        } catch (e) {
          /* private mode: it shows again next visit */
        }
        this.teaserAbove = this.isMobile && !!document.querySelector('.beat-player');
        this.teaserMini = !!document.querySelector('.beat-mini');
        this.teaser = this.pagePrompts[0];
        this.teaserHideTimer = setTimeout(() => (this.teaser = ''), 6000);
      }, 1500);
    },
    askTeaser() {
      const question = this.teaser;
      this.teaser = '';
      this.openChat();
      this.$nextTick(() => this.sendMessage(question));
    },
    resolveCard(route) {
      const entry = CARD_MANIFEST[route];
      if (!entry) return null;
      return { ...entry, image: this.cardImage(entry.thumbnail) };
    },
    // Thumbnails are bundled assets, resolved the way the site's other cards do
    // it. The build step already checks each path exists; this guard keeps a
    // file that goes missing later from taking the whole reply down with it.
    cardImage(thumbnail) {
      if (!thumbnail) return null;
      try {
        return require(`../assets/images/${thumbnail}`);
      } catch (e) {
        return null;
      }
    },
    // The metadata sent with each message, read at send time rather than
    // cached, so it names the page the visitor is on now: the chat stays
    // open across pages, and the agent should know where they are.
    metadataNow() {
      const safeLocation =
        typeof window !== 'undefined' && window?.location?.href ? window.location.href : null;
      const safeReferrer =
        typeof document !== 'undefined' && document?.referrer ? document.referrer : null;
      const safeUserAgent =
        typeof navigator !== 'undefined' && navigator?.userAgent ? navigator.userAgent : null;
      const safeTimezone =
        typeof Intl !== 'undefined' && Intl?.DateTimeFormat
          ? Intl.DateTimeFormat().resolvedOptions().timeZone
          : null;

      return {
        ...(this.metadata || {}),
        url: safeLocation,
        page: {
          path: this.$route?.path ?? (typeof window !== 'undefined' ? window.location.pathname : null),
          title: typeof document !== 'undefined' ? document.title : null,
        },
        referrer: safeReferrer,
        userAgent: safeUserAgent,
        timezone: safeTimezone,
        timestamp: new Date().toISOString(),
      };
    },
    // Left-click navigates in-app. The chat stays open beside the page, except
    // fullscreen (phones), where it would cover the page you asked for.
    // Modifier and middle clicks fall through to the browser so "open in new
    // tab" still works, and so does the card outside a router (Storybook).
    handleCardClick(card, event) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (event.button && event.button !== 0) return;
      if (!this.$router) return;
      event.preventDefault();
      if (this.isFullscreen) this.closeChat();
      this.$router.push(card.route).catch(() => {});
    },
    formatMessage(text, role) {
      const raw = String(text == null ? '' : text);
      if (this.renderHTML) {
        return raw;
      }
      // Bot replies are markdown from Claude — render them. Escape angle
      // brackets first so any raw/injected HTML can't execute (defence for
      // v-html), then let marked handle the markdown syntax. `marked` is
      // loaded lazily; until it's ready, fall back to escaped text.
      if (role === 'bot') {
        const escaped = raw.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        if (!this.markedLib) {
          this.ensureMarked();
          return escaped.replace(/\n/g, '<br>');
        }
        const renderer = new this.markedLib.Renderer();
        // Only allow safe link schemes; drop javascript:/data: etc.
        renderer.link = (href, title, textHtml) => {
          const safe = /^(https?:\/\/|mailto:|\/|#)/i.test(href || '');
          if (!safe) return textHtml;
          const t = title ? ` title="${title}"` : '';
          return `<a href="${href}"${t}>${textHtml}</a>`;
        };
        try {
          return this.markedLib.parse(escaped, {
            breaks: true,
            renderer,
            mangle: false,
            headerIds: false,
          });
        } catch (e) {
          return escaped.replace(/\n/g, '<br>');
        }
      }
      // User (and everything else): escape, preserve newlines. No markdown.
      const div = document.createElement('div');
      div.textContent = raw;
      return div.innerHTML.replace(/\n/g, '<br>');
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.messagesContainer) {
          this.$refs.messagesContainer.scrollTop = this.$refs.messagesContainer.scrollHeight;
        }
      });
    },
    handleResize() {
      // Keep viewport-derived computed props reactive (prevents "sticky" mobile/desktop behavior)
      this.viewportWidth = window.innerWidth;

      if (!this.isOpen) return;

      // If the viewport becomes mobile-sized while chat is open, force fullscreen.
      if (this.fullscreenOnMobile && this.isMobile) {
        if (!this.isFullscreen) {
          this.isFullscreen = true;
          this.fullscreenForcedByMobile = true;
          this.syncFullscreenRootClass();
        }
        return;
      }
      // If fullscreen was forced by mobile, revert to windowed when leaving mobile width.
      if (this.fullscreenForcedByMobile && this.isFullscreen) {
        this.isFullscreen = false;
        this.fullscreenForcedByMobile = false;
        this.syncFullscreenRootClass();
      }
      // Desktop: do not auto-enter fullscreen on resize.
    },
    // handleMobileScroll() — fed the old mobile CTA's show/hide. Disabled with it.
    // handleMobileScroll() {
    //   const threshold = 80;
    //   this.atBottomOfPage =
    //     window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - threshold;
    // },
    handleEscapeKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        // On mobile we treat fullscreen as the default state, so Escape should close.
        if (this.fullscreenOnMobile && this.isMobile) {
          this.closeChat();
          return;
        }
        if (this.isFullscreen) {
          this.isFullscreen = false;
          this.fullscreenForcedByMobile = false;
          this.syncFullscreenRootClass();
        } else {
          this.closeChat();
        }
      }
    },
  },
  mounted() {
    // Initialize viewportWidth + keep it updated (even when chat is closed)
    this.viewportWidth = window.innerWidth;
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.handleEscapeKey);
    // Scroll listener drove the old mobile CTA's show/hide — no longer needed.
    // window.addEventListener('scroll', this.handleMobileScroll, { passive: true });

    // Sync initial bottom-of-page state
    // this.handleMobileScroll();

    // Watch for menu-open class set by FullscreenMenu
    this.menuIsOpen = document.documentElement.classList.contains('menu-open');
    this._menuObserver = new MutationObserver(() => {
      this.menuIsOpen = document.documentElement.classList.contains('menu-open');
    });
    this._menuObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Load messages from localStorage if not clearing on reload
    if (!this.clearChatOnReload) {
      const savedMessages = localStorage.getItem(`chat-messages-${this.webhookUrl}`);
      if (savedMessages) {
        try {
          this.messages = JSON.parse(savedMessages);
        } catch (e) {
          console.error('Error loading saved messages:', e);
        }
      }
    }

    // Note: desktop should not auto-open fullscreen; fullscreen is user-selected.
  },
  beforeUnmount() {
    clearTimeout(this.pageLabelTimer);
    clearTimeout(this.teaserTimer);
    clearTimeout(this.teaserHideTimer);
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleEscapeKey);
    // window.removeEventListener('scroll', this.handleMobileScroll);
    if (this._menuObserver) this._menuObserver.disconnect();
    // Clean up just in case
    if (typeof document !== 'undefined') {
      document.documentElement.classList.remove('chat-fullscreen-open');
    }
    this.unlockBodyScroll();
  },
  watch: {
    // The title updates after the route does (useHead), so read it a beat later.
    '$route.path': {
      handler() {
        clearTimeout(this.pageLabelTimer);
        this.pageLabelTimer = setTimeout(this.updatePageLabel, 400);
        this.scheduleTeaser();
      },
      immediate: true,
    },
    isOpen(open) {
      if (open) {
        this.updatePageLabel();
        this.teaser = '';
      }
    },
    messages: {
      handler(newMessages) {
        // Save messages to localStorage
        if (!this.clearChatOnReload) {
          localStorage.setItem(`chat-messages-${this.webhookUrl}`, JSON.stringify(newMessages));
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss">
.custom-chat-ui {
  position: relative;
  z-index: 9999;
  font-family: var(--fontFamily-primary);

  /* Component-local tokens (mapped to your global tokens) */
  --chat-surface: var(--background);
  --chat-surface-2: var(--background-darker);
  --chat-ink: var(--foreground);
  --chat-border: var(--border);
  --chat-shadow: var(--shadow-deep);
  --chat-shadow-hover: var(--shadow-hover);
  --chat-radius: var(--spacing-xs);
  --chat-radius-lg: var(--spacing-sm);
  /* Reading width for the expanded view. The chrome (scroller, the rule above
     the input) runs full-bleed; this caps only what you read. */
  --chat-content-max: 800px; /* design-guard:ignore */
  /* Focus ring uses your action color token */
  --chat-ring: 0 0 0 0.3rem rgba(100, 21, 255, 0.18); /* design-guard:ignore */
}

@supports (color: color-mix(in srgb, #000 50%, transparent)) {
  /* design-guard:ignore */
  .custom-chat-ui {
    --chat-ring: 0 0 0 0.3rem color-mix(in srgb, var(--color-action) 22%, transparent); /* design-guard:ignore */
  }
}

/* Chat Button */
.chat-button {
  position: fixed;
  z-index: 9998;
  display: block;
  border-radius: var(--spacing-xxs);
  border: var(--chat-border);
}

/* The page's first question, beside the button: the page's ink on the page,
   with the button's border, so it reads as the button speaking. */
.chat-teaser {
  position: fixed;
  z-index: 9998;
  display: block;
  box-sizing: content-box;
  margin: 0;
  padding: 0 var(--spacing-xs);
  /* One line, the height of the icon; a long question is cut short. */
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border: var(--chat-border);
  border-radius: var(--spacing-xxs);
  background: var(--background);
  color: var(--foreground);
  font: inherit;
  font-size: var(--font-400);
  font-weight: var(--font-400-weight);
  text-align: start;
  cursor: pointer;
  box-shadow: var(--shadow-z2);
}

.chat-teaser:focus-visible {
  outline: none;
  box-shadow: var(--shadow-z2), var(--chat-ring);
}

.chat-teaser-enter-active,
.chat-teaser-leave-active {
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;
}

.chat-teaser-enter-from,
.chat-teaser-leave-to {
  opacity: 0;
  transform: translateX(var(--spacing-xs));
}

@media (prefers-reduced-motion: reduce) {
  .chat-teaser-enter-active,
  .chat-teaser-leave-active {
    transition: opacity 0.2s linear;
  }
  .chat-teaser-enter-from,
  .chat-teaser-leave-to {
    transform: none;
  }
}

.chat-button-bar-label {
  display: none;
}

/* Mobile text-CTA styles — commented out. The icon FAB is now the single entry
   point on every breakpoint, so the old safeguard that hid it on mobile (and the
   full-width bar + scroll show/hide rules) are no longer needed.
@media (max-width: 768px) {
  .chat-button:not(.chat-button--mobile) {
    display: none !important;
  }

  .chat-button--mobile {
    position: fixed;
    bottom: var(--spacing-xxs);
    left: var(--spacing-xxs);
    right: var(--spacing-xxs);
    width: calc(100% - var(--spacing-xxs) * 2);
    height: auto;
    transition:
      opacity 0.25s ease,
      transform 0.25s ease;
    z-index: 9998;
    display: block;
  }

  .chat-button--mobile > span,
  .chat-button--mobile .custom-btn {
    display: block;
    width: 100%;
  }

  .chat-button--hidden {
    opacity: 0;
    transform: translateY(calc(100% + var(--spacing-md)));
    pointer-events: none;
  }
}
*/

.chat-button-desktop-wrapper {
  position: fixed;
  bottom: var(--spacing-sm);
  right: var(--spacing-xs);
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  z-index: 9998;
}

.chat-button--desktop {
  display: block;
  width: fit-content;
  transform: rotate(-90deg);
  transform-origin: top center;
  align-self: flex-end;
}

.chat-button--desktop .custom-btn:hover:not(:disabled) {
  color: var(--foreground) !important;
  background: var(--background) !important;
  border-color: var(--foreground) !important;
  opacity: 1;
}

.chat-button:not(.chat-button--mobile) .custom-btn {
  /* Layout */
  width: 100%;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  /* Colors — reversed tokens (theme-invariant contrast) */
  color: var(--foreground);
  background: var(--background);

  /* Borders */
  border: none;
  border-radius: var(--spacing-xxs);

  /* Effects */
  box-shadow: var(--shadow-z4);

  /* Interaction */
  cursor: pointer;
  transition: all 0.2s ease;
}

.chat-button .custom-btn:hover:not(:disabled) {
  background: var(--background) !important;
}

.chat-button .custom-btn:active:not(:disabled) {
  transform: scale(0.95);
}

.chat-button .custom-btn:focus-visible {
  outline: none;
  box-shadow: var(--shadow-z4), var(--chat-ring);
}

.chat-button .chat-button-icon {
  width: 60%; /* design-guard:ignore */
  height: 60%; /* design-guard:ignore */
  object-fit: contain;
}

.chat-button img.chat-button-icon {
  /* For custom icons provided via buttonIconSrc - use filter approach */
  filter: var(--icon-filter);
}

.chat-button svg.chat-button-icon {
  /* For default inline SVG - use reversed foreground to match button */
  color: var(--foreground);
  stroke: currentColor;
  /* The site's line weight at any icon size: the stroke is measured on
     screen, not in the 24-unit viewBox. */
  stroke-width: var(--line-drawn);

  path {
    vector-effect: non-scaling-stroke;
  }
}

.chat-button-icon--hover {
  display: none;
}
.chat-button-icon--active {
  display: none;
}
.chat-button:hover .chat-button-icon--default {
  display: none;
}
.chat-button:hover .chat-button-icon--hover {
  display: block;
}
.chat-button:active .chat-button-icon--default {
  display: none;
}
.chat-button:active .chat-button-icon--hover {
  display: none;
}
.chat-button:active .chat-button-icon--active {
  display: block;
}

/* Chat Window */
.chat-window {
  position: fixed;
  display: flex;
  flex-direction: column;
  box-shadow: var(--chat-shadow);
  z-index: 9999;
  overflow: hidden;
  overflow-x: hidden;
  color: var(--chat-ink);
  background: var(--chat-surface);
  border: var(--chat-border);
  border-radius: var(--chat-radius-lg);
  -webkit-backdrop-filter: blur(10px); /* design-guard:ignore */
  backdrop-filter: blur(10px); /* design-guard:ignore */
  max-height: min(76vh, var(--size-36)); /* design-guard:ignore */
  max-width: min(92vw, 44rem); /* design-guard:ignore */
  transform-origin: bottom right;
  box-sizing: border-box;

  * {
    box-sizing: border-box;
  }

  &--fullscreen {
    /* Header nav is z-index: 100000; ensure chat overlays everything */
    z-index: 100001;
    bottom: 0;
    right: 0;
    top: 0;
    left: 0;
    transform-origin: center;
  }

  &--pos-bottom-right {
    transform-origin: bottom right;
  }
  &--pos-bottom-left {
    transform-origin: bottom left;
  }
  &--pos-top-right {
    transform-origin: top right;
  }
  &--pos-top-left {
    transform-origin: top left;
  }

  @media (max-width: 768px) {
    /* design-guard:ignore */
    bottom: 0;
    right: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    max-width: 100% !important;
    max-height: 100% !important;
    border-radius: 0 !important;
  }
}

/* Chat header — outer wrapper mirrors HeaderNav .bg (padding only) */
.chat-floating-actions {
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  padding-block: var(--spacing-xs) !important;
  padding-inline: var(--spacing-sm);
  background: var(--chat-surface);
  /* No rule under the header: the input is the only outlined thing in the sheet. */
  z-index: 2;
  box-sizing: border-box;

  /* Fullscreen: match HeaderNav responsive padding exactly */
  .chat-window--fullscreen & {
    @media only screen and (min-width: 768px) {
      /* design-guard:ignore */
      padding-inline-start: var(--spacing-lg);
      padding-inline-end: var(--spacing-sm);
    }

    @media only screen and (min-width: 1201px) {
      /* design-guard:ignore */
      padding-inline-start: var(--spacing-xl);
      padding-inline-end: var(--spacing-sm);
    }
  }
}

/* Chat header — inner nav mirrors HeaderNav nav (grid only) */
.chat-nav {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--spacing-md);
}

.chat-nav-left {
  display: flex;
  flex-direction: row;
  gap: var(--spacing-xs);
  align-items: center;
}

/* Mobile sheet header: ✕ left, title centred, Clear right. Equal outer
   columns keep the title optically centred whatever the side widths are. */
.chat-nav--sheet {
  grid-template-columns: 1fr auto 1fr;
  gap: var(--spacing-xs);

  .chat-nav-close {
    justify-self: start;
  }

  /* Keep the ghost button's tap area but pull it back so it doesn't make
     the header taller than the text-only version. */
  .chat-nav-close .custom-btn {
    padding: var(--spacing-xxs);
    margin: calc(-1 * var(--spacing-xxs));
  }

  /* Built like HeaderNav's .menu-icon, at its size and bar weight, so the
     sheet's close reads as the same control in its open state. */
  .chat-nav-close-icon {
    --bar: var(--line-width);
    position: relative;
    display: block;
    font-size: var(--font-600);
    --reading-fontWeight: var(--font-600-weight);
    font-weight: var(--font-600-weight);
    inline-size: round(1.1em, 1px);
    block-size: round(0.45em, 2px);

    span {
      position: absolute;
      inset-inline: 0;
      top: calc(50% - var(--bar) / 2);
      block-size: 0;
      border-block-start: var(--bar) solid currentColor; /* snaps like a border, as .menu-icon's bars do */
      animation: chat-close-cross-first 0.25s ease 0.2s both;
    }
    span:first-child {
      transform: rotate(45deg);
    }
    span:last-child {
      transform: rotate(-45deg);
      animation-name: chat-close-cross-last;
    }
  }

  .chat-header-title {
    text-align: center;
  }

  .chat-nav-clear {
    justify-self: end;
  }
}

.chat-nav-avatar-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none !important;
}

.chat-nav-avatar {
  inline-size: var(--spacing-md);
  block-size: var(--spacing-md);
  aspect-ratio: 1 / 1;
  border-radius: 100%;
  object-fit: cover;
  box-shadow: var(--shadow-light);
}

.chat-header-title {
  /* No type overrides — it's a plain <p>, so it inherits the global body/<p>
     tokens (same size as the nav wordmark and nav links). */
  margin: 0;
  padding: 0;
}

.chat-nav-links {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-self: flex-end;
  gap: var(--spacing-sm);

  li {
    list-style: none;
    margin: 0;
    line-height: var(--lineHeight-normal);
  }
}

/* Chat Messages */
.chat-messages {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  scroll-behavior: smooth;
  /* Match site default <p> scale */
  font-size: var(--font-500);
  --reading-fontWeight: var(--font-500-weight);
  font-weight: var(--font-500-weight);
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  /* Fullscreen: the scroller itself stays full width so its scrollbar sits at
     the edge of the screen, and the cap moves to the content inside it. */
  .chat-window--fullscreen & {
    padding-left: var(--spacing-sm);
    padding-right: var(--spacing-sm);

    > * {
      width: 100%;
      max-width: var(--chat-content-max);
      margin-inline: auto;
    }
  }
}

// Inline padding matches the starter prompts, so the intro's left edge lines
// up with the prompt text below it.
.chat-welcome-text {
  text-align: left;
  color: inherit;
  opacity: 0.7;
  margin: 0;
  padding: var(--spacing-sm) var(--spacing-xs);
}

/* A quiet line above the input naming the page the guide can see. */
.chat-page-indicator {
  display: flex;
  align-items: center;
  gap: var(--spacing-xxxs);
  min-width: 0;
  margin: 0;
  padding: var(--spacing-xxs) var(--spacing-xs) 0;
  font-size: var(--font-400);
  --reading-fontWeight: var(--font-400-weight);
  font-weight: var(--font-400-weight);
  color: inherit;
  box-sizing: border-box;

  .chat-window--fullscreen & {
    width: 100%;
    max-width: var(--chat-content-max);
    margin-inline: auto;
  }
}

.chat-page-indicator-dot {
  flex: 0 0 auto;
  inline-size: var(--size-2);
  block-size: var(--size-2);
  border-radius: 50%; /* design-guard:ignore */
  background-color: currentColor;
}

.chat-page-indicator-label {
  flex: 0 0 auto;
  opacity: 0.7;
}

.chat-page-indicator-title {
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-starter-prompts {
  display: flex;
  flex-direction: column;
  gap: 0.75rem; /* design-guard:ignore */
  margin-bottom: var(--spacing-field);
  width: 100%;
}

.chat-starter-prompt .custom-btn {
  width: 100%;
  text-align: left;
  padding: var(--spacing-xxs) var(--spacing-xs);
  background-color: var(--chat-surface-2);
  border: var(--chat-border);
  border-radius: var(--chat-radius);
  cursor: pointer;
  font-size: inherit;
  --reading-fontWeight: inherit;
  transition:
    transform 0.2s,
    box-shadow 0.2s,
    background-color 0.2s;
  color: inherit;
}

.chat-starter-prompt .custom-btn:hover:not(:disabled) {
  box-shadow: var(--shadow-z2);
  transform: translateX(var(--spacing-xxxs));
}

.chat-starter-prompt .custom-btn:focus-visible {
  outline: none;
  box-shadow: var(--shadow-z2), var(--chat-ring);
}

.chat-message {
  display: flex;
  gap: 0.75rem; /* design-guard:ignore */
  align-items: flex-start;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 0;
  margin: 0;

  &--welcome {
    max-width: 100%;
    justify-content: flex-start;
  }

  &--bot {
    align-self: flex-start;
    justify-content: flex-start;
  }

  &--user {
    align-self: flex-end;
    justify-content: flex-end;
  }
}

/* One message and any cards that came with it. Repeats the message
   container's own gap, so spacing is unchanged for messages without cards. */
.chat-message-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  width: 100%;
  min-width: 0;
}

/* Rich cards. A sibling of the message rather than a child of the bubble, so
   they take the full column width instead of the bubble's inset. Styled off
   the starter prompts so the two clickable things in the chat match. */
.chat-cards {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxs);
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.chat-card-item {
  min-width: 0;
}

.chat-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xxs);
  min-width: 0;
  color: inherit;
  text-decoration: none;
  background-color: var(--chat-surface-2);
  border: var(--chat-border);
  border-radius: var(--chat-radius);
}

/* Plenty of published entries have no thumbnail, so this is a normal card
   rather than an edge case. Without the extra padding it collapses to half the
   height of the cards beside it and reads as broken. */
.chat-card--text-only {
  padding: var(--spacing-xs);
}

/* The whole card is the target, but only the title carries the affordance —
   underlining the eyebrow or the description would read as three links. */
.chat-card:hover .chat-card-title,
.chat-card:focus-visible .chat-card-title {
  text-decoration: underline;
}

/* Keyboard focus still needs a visible ring; that is not the hover effect. */
.chat-card:focus-visible {
  outline: none;
  box-shadow: var(--chat-ring);
}

.chat-card-thumb {
  flex: 0 0 auto;
  inline-size: var(--size-17);
  block-size: var(--size-15);
  object-fit: cover;
  /* Concentric radii: the inner curve is the outer one minus the padding
     between them, or the image looks like it bulges past the card's corner.
     Card radius is --spacing-xs and the padding is --spacing-xxs, which is
     half of it, so the two are the same value here. */
  border-radius: var(--spacing-xxs);
  /* Behind a transparent PNG, and the placeholder while it loads. */
  background-color: var(--background);
}

.chat-card-body {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xxxs);
  min-width: 0;
}

.chat-card-eyebrow {
  font-size: var(--font-400);
  --reading-fontWeight: var(--font-400-weight);
  font-weight: var(--font-400-weight);
  opacity: 0.7;
}

.chat-card-title {
  font-weight: var(--fontWeight-normal);
  /* Long single-word titles must wrap rather than widen the chat column. */
  overflow-wrap: anywhere;
}

/* One line, cut with an ellipsis. Truncating in CSS rather than in the
   manifest means the cut follows the chat's actual width, so the fullscreen
   view shows more of the sentence than the docked panel does. */
.chat-card-description {
  font-size: var(--font-400);
  --reading-fontWeight: var(--font-400-weight);
  font-weight: var(--font-400-weight);
  opacity: 0.7;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-message-bubble {
  padding: var(--spacing-xxs) var(--spacing-xs);
  line-height: var(--lineHeight-taller);
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  border: none;
  box-shadow: none;
  font-size: inherit;
  --reading-fontWeight: inherit;
  background: transparent;
  min-width: 0;
  box-sizing: border-box;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: var(--fontWeight-medium);

  a {
    color: inherit !important;
    text-decoration-thickness: var(--link-underline-thickness-hover) !important;
    text-underline-offset: 0.35rem; /* design-guard:ignore */
    overflow-wrap: break-word;
  }

  * {
    max-width: 100%;
    overflow-wrap: break-word;
    box-sizing: border-box;
  }
}

.chat-message-bubble--user {
  /* User: light grey bg, no border */
  background: var(--background-darker) !important;
  color: var(--foreground);
  text-align: left;
}

.chat-message-bubble--bot {
  /* Bot: no bg, no border */
  background: transparent;
  padding: 0;
  overflow: visible; /* base bubble clips overflow — let list markers show */

  /* Rendered markdown from the assistant — compact, mostly body-size.
     Non-scoped <style>, so plain descendant selectors (no :deep). Heading
     sizes use em (relative to the chat font) so they scale down on mobile
     and never render at the site's banner heading sizes. */
  p {
    margin: 0 0 var(--spacing-xs);
    font-size: 1em; /* override the global "h1 + p" lead-paragraph enlargement */
    --reading-fontWeight: inherit;
  }
  p:last-child {
    margin-bottom: 0;
  }
  ul,
  ol {
    margin: 0 0 var(--spacing-xs);
    padding-inline-start: var(--spacing-md);
    list-style-position: outside;
  }
  ul {
    list-style-type: disc !important;
  }
  ol {
    list-style-type: decimal !important;
  }
  ul ul {
    list-style-type: circle !important;
  }
  li {
    margin: 0 0 var(--spacing-xxxs);
  }
  li::marker {
    color: inherit;
  }
  li:last-child {
    margin-bottom: 0;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 1em !important; /* everything at paragraph size — headings are just bold */
    --reading-fontWeight: inherit;
    font-weight: var(--fontWeight-bold);
    line-height: var(--lineHeight-normal);
    letter-spacing: normal !important; /* beat global heading letter-spacing !important */
    font-variation-settings: normal;
    font-family: inherit; /* not the display font */
    margin: var(--spacing-sm) 0 var(--spacing-xxxs);
  }
  h1:first-child,
  h2:first-child,
  h3:first-child,
  h4:first-child,
  h5:first-child,
  h6:first-child {
    margin-top: 0;
  }
  strong {
    font-weight: var(--fontWeight-bold);
  }
  em {
    font-style: italic;
  }
  code {
    font-family: var(--fontFamily-mono, monospace); /* design-guard:ignore */
    font-size: var(--font-inline-code);
    background: var(--background-darker);
    padding: 0 var(--spacing-xxxs);
    border-radius: 3px; /* design-guard:ignore */
  }
  pre {
    overflow-x: auto;
    background: var(--background-darker);
    padding: var(--spacing-xs);
    border-radius: var(--chat-radius);
    margin: 0 0 var(--spacing-xs);
  }
  pre code {
    background: transparent;
    padding: 0;
  }
  blockquote {
    margin: 0 0 var(--spacing-xs);
    padding-inline-start: var(--spacing-xs);
    border-inline-start: var(--spacing-xxxs) solid currentColor;
    opacity: 0.85;
  }
}

.chat-loading {
  display: flex;
  gap: var(--spacing-xxxs);
  padding: var(--spacing-xs) var(--spacing-sm);

  span {
    width: var(--size-2);
    height: var(--size-2);
    border-radius: 50%; /* design-guard:ignore */
    background-color: currentColor;
    animation: chat-loading 1.4s infinite ease-in-out;

    &:nth-child(1) {
      animation-delay: -0.32s;
    }

    &:nth-child(2) {
      animation-delay: -0.16s;
    }
  }
}

@keyframes chat-loading {
  0%,
  80%, /* design-guard:ignore */
  100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    /* design-guard:ignore */
    transform: scale(1);
    opacity: 1;
  }
}

.chat-error {
  padding: var(--spacing-sm);
  background-color: var(--chat-surface-2);
  color: var(--chat-ink);
  border: var(--chat-border);
  border-left: var(--spacing-xxxs) solid var(--color-danger);
  border-radius: var(--chat-radius);
  margin-top: var(--spacing-field);

  p {
    margin: 0;
  }
}

.chat-warning {
  padding: var(--spacing-xxs) var(--spacing-sm);
  background-color: var(--chat-surface-2);
  color: var(--chat-ink);
  border-top: var(--chat-border);
  border-left: var(--spacing-xxxs) solid var(--color-yellow);
  font-size: var(--font-400); /* was 0.875rem, which came out at 8.75px on the 10px root */
  --reading-fontWeight: var(--font-400-weight);
  font-weight: var(--font-400-weight);
  text-align: center;

  p {
    margin: 0;
  }

  /* Max width for fullscreen on desktop */
  .chat-window--fullscreen & {
    max-width: 800px; /* design-guard:ignore */
    margin-left: auto;
    margin-right: auto;
    padding-left: var(--spacing-md);
    padding-right: var(--spacing-md);
    box-sizing: border-box;
  }
}

/* Chat Input */
.chat-input-container {
  display: flex;
  padding-block: var(--spacing-xxs);
  /* No rule above it either; the input's own outline is the edge. */
  background: var(--chat-surface);
  box-sizing: border-box;
  @media only screen and (min-width: 768px) {
    padding-inline: var(--spacing-xxs);
  }

  /* Fullscreen: the container runs edge to edge so its top rule spans the
     whole screen, and the cap goes on MyInput's root element. That root is an
     unclassed div — `chat-input-wrapper` is passed through to the <input>
     inside it — so this targets the child rather than a class. */
  .chat-window--fullscreen & {
    justify-content: center;
    padding-left: var(--spacing-xxs);
    padding-right: var(--spacing-xxs);
    box-sizing: border-box;
    width: 100%;

    > * {
      width: 100%;
      max-width: var(--chat-content-max);
    }
  }
}

.chat-input-wrapper {
  flex: 1;
  min-width: 0;
  width: 100%;
}

.chat-input {
  width: 100%;
  max-width: 100%;

  &:focus {
    border-color: var(--link);
  }

  &:focus-visible {
    box-shadow: var(--chat-ring);
  }
}

:root.chat-fullscreen-open #headernav {
  z-index: 0 !important;
}

:root.chat-fullscreen-open,
:root.chat-fullscreen-open body {
  /* Prevent background scrolling when chat is fullscreen */
  overflow: hidden !important;
  height: 100%;
  overscroll-behavior: none;
}

.chat-backdrop {
  position: fixed;
  inset: 0;
  z-index: 100000;
  pointer-events: none;
  background: rgba(0, 0, 0, 0.18); /* design-guard:ignore */
  -webkit-backdrop-filter: blur(2px); /* design-guard:ignore */
  backdrop-filter: blur(2px); /* design-guard:ignore */
}

@supports (color: color-mix(in srgb, #000 50%, transparent)) {
  /* design-guard:ignore */
  .chat-backdrop {
    background: color-mix(in srgb, var(--background) 12%, transparent); /* design-guard:ignore */
  }
}

/* Transitions */
.chat-window-enter-active,
.chat-window-leave-active {
  transition: all 0.3s ease;
}

.chat-window-enter-from {
  opacity: 0;
  transform: scale(0.25);
}

.chat-window-enter-to {
  opacity: 1;
  transform: scale(1);
}

.chat-window-leave-from {
  opacity: 1;
  transform: scale(1);
}

.chat-window-leave-to {
  opacity: 0;
  transform: scale(0.25);
}

/* Mobile: slide up as a sheet, slide back down on close. */
.chat-sheet-enter-active {
  transition: transform 0.28s cubic-bezier(0.32, 0.72, 0, 1);
}
.chat-sheet-leave-active {
  transition: transform 0.22s ease-in;
}
.chat-sheet-enter-from,
.chat-sheet-leave-to {
  transform: translateY(100%);
}
/* The close icon uncrosses back to = as the sheet slides away. Its own
   keyframes, since reversing the finished entrance wouldn't restart it. */
.chat-sheet-leave-active .chat-nav-close-icon span {
  animation: chat-close-uncross-first 0.22s ease both;
}
.chat-sheet-leave-active .chat-nav-close-icon span:last-child {
  animation-name: chat-close-uncross-last;
}

/* From the menu icon's = (bars at top and bottom, level) to its ×. */
@keyframes chat-close-cross-first {
  from {
    top: 0;
    transform: none;
  }
}
@keyframes chat-close-cross-last {
  from {
    top: calc(100% - var(--bar));
    transform: none;
  }
}
@keyframes chat-close-uncross-first {
  to {
    top: 0;
    transform: none;
  }
}
@keyframes chat-close-uncross-last {
  to {
    top: calc(100% - var(--bar));
    transform: none;
  }
}

.chat-backdrop-enter-active,
.chat-backdrop-leave-active {
  transition: opacity 0.25s ease;
}
.chat-backdrop-enter-from,
.chat-backdrop-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  /* Swap motion for a short fade rather than removing feedback entirely. */
  .chat-sheet-enter-active,
  .chat-sheet-leave-active {
    transition: opacity 0.15s ease !important;
  }
  .chat-nav-close-icon span {
    animation: none !important;
  }
  .chat-sheet-enter-from,
  .chat-sheet-leave-to {
    transform: none;
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chat-button,
  .chat-window-enter-active,
  .chat-window-leave-active,
  .chat-starter-prompt,
  .chat-send-button,
  .chat-header-button {
    transition: none !important;
  }
}

/* Scrollbar Styling */
.chat-messages::-webkit-scrollbar {
  width: 6px; /* design-guard:ignore */
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
  background: var(--color-xlight);
  border-radius: 3px; /* design-guard:ignore */

  &:hover {
    background: var(--color-light);
  }
}
</style>
